import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import MultiProcedureSelector from '../../common/MultiProcedureSelector';
import PatientService from '../../../services/patientService';
import DentalCertificateService from '../../../services/dentalCertificateService';
import './NewCertificate.css';

const NewCertificate = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], // Today's date
    procedures: [], // Array of selected procedures with quantities
    observations: ''
  });

  const loadPatient = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const patientData = await PatientService.getPatientById(patientId, token);
      setPatient(patientData);
    } catch (error) {
      console.error('Error loading patient:', error);
      setError('Error al cargar información del paciente');
    } finally {
      setIsLoadingData(false);
    }
  }, [patientId, auth.user?.access_token]);

  useEffect(() => {
    loadPatient();
  }, [loadPatient]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProceduresChange = (procedures) => {
    setFormData(prev => ({
      ...prev,
      procedures: procedures
    }));
  };

  const validateForm = () => {
    if (!formData.date) {
      setMessage({
        type: 'error',
        text: 'La fecha es requerida'
      });
      return false;
    }

    if (!formData.procedures || formData.procedures.length === 0) {
      setMessage({
        type: 'error',
        text: 'Debe seleccionar al menos un procedimiento'
      });
      return false;
    }

    return true;
  };

  const handlePreview = () => {
    if (!validateForm()) {
      return;
    }

    const certificateData = {
      id: 'PREVIEW',
      date: formData.date,
      procedures: formData.procedures,
      observations: formData.observations
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateCertificateHTML(certificateData));
    printWindow.document.close();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      // Clean procedures data - only keep what's needed for certificates
      const cleanedProcedures = formData.procedures.map(proc => ({
        procedureId: proc.procedureId,
        procedureName: proc.procedureName,
        quantity: proc.quantity || 1
      }));
      
      const certificateData = {
        patientId,
        date: formData.date,
        procedures: cleanedProcedures,
        observations: formData.observations
      };
      
      await DentalCertificateService.createDentalCertificate(certificateData, token);
      
      // Navigate immediately after successful save (like other forms)
      navigate(`/patients/${patientId}/certificates`);

    } catch (error) {
      console.error('Error creating certificate:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Error al crear el certificado. Inténtalo de nuevo.'
      });
      setIsLoading(false); // Only disable loading on error
    }
  };

  const handleCancel = () => {
    navigate(`/patients/${patientId}/certificates`);
  };

  const generateCertificateHTML = (certificate) => {
    const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
    const certificateDate = certificate.date ? new Date(certificate.date).toLocaleDateString('es-ES') : new Date().toLocaleDateString('es-ES');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Certificado Dental - ${patientName}</title>
        <style>
          body {
            font-family: 'Times New Roman', serif;
            margin: 20px;
            line-height: 1.4;
            color: #333;
            background: #fff;
          }
          .certificate-container {
            max-width: 700px;
            margin: 0 auto;
            border: 2px solid #2d3748;
            padding: 25px;
            position: relative;
            min-height: calc(11in - 40px);
            box-sizing: border-box;
          }
          .certificate-header {
            text-align: center;
            border-bottom: 1px solid #2d3748;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .clinic-logo {
            width: 60px;
            height: 60px;
            background: #f7fafc;
            border: 2px solid #2d3748;
            border-radius: 50%;
            margin: 0 auto 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: bold;
            color: #2d3748;
          }
          .clinic-info h1 {
            font-size: 22px;
            font-weight: bold;
            color: #2d3748;
            margin: 8px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .clinic-info h2 {
            font-size: 16px;
            color: #4a5568;
            margin: 3px 0;
            font-weight: normal;
          }
          .doctor-credentials {
            font-size: 12px;
            color: #718096;
            margin: 8px 0;
          }
          .certificate-title {
            font-size: 24px;
            font-weight: bold;
            color: #2d3748;
            margin: 15px 0;
            text-transform: uppercase;
            letter-spacing: 2px;
            text-decoration: underline;
          }
          .certificate-content {
            font-size: 14px;
            line-height: 1.5;
            text-align: justify;
            margin: 20px 0;
            padding: 10px 0;
          }
          .patient-name {
            font-weight: bold;
            text-transform: uppercase;
            text-decoration: underline;
          }

          .signature-section {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
            align-items: end;
          }
          .date-section {
            text-align: left;
            font-size: 14px;
          }
          .signature-box {
            text-align: center;
            width: 200px;
          }
          .signature-line {
            border-top: 2px solid #2d3748;
            margin: 40px 0 10px 0;
          }
          .doctor-name {
            font-weight: bold;
            font-size: 14px;
            color: #2d3748;
          }
          .doctor-title {
            font-size: 12px;
            color: #4a5568;
            margin-top: 3px;
          }
          .certificate-footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            color: #718096;
            font-size: 10px;
          }
          .contact-info {
            margin: 10px 0;
          }
          .certificate-number {
            position: absolute;
            top: 15px;
            right: 15px;
            font-size: 12px;
            color: #718096;
          }
          .preview-watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 48px;
            color: rgba(255, 0, 0, 0.1);
            font-weight: bold;
            z-index: 10;
            pointer-events: none;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 0;
            }
            .certificate-container { 
              margin: 15px;
              padding: 20px;
              break-inside: avoid;
              box-shadow: none;
              page-break-inside: avoid;
              max-height: calc(11in - 30px);
              overflow: hidden;
            }
            .preview-watermark { display: none; }
            .signature-section {
              margin-top: 20px;
            }
            .certificate-footer {
              margin-top: 15px;
              font-size: 9px;
            }
          }
        </style>
      </head>
      <body>
        <div class="certificate-container">
          ${certificate.id === 'PREVIEW' ? '<div class="preview-watermark">VISTA PREVIA</div>' : ''}
          
          <div class="certificate-number">
            Certificado No. ${certificate.id || 'XXXX'}
          </div>
          
          <div class="certificate-header">
            <div class="clinic-logo">
              🦷
            </div>
            <div class="clinic-info">
              <h1>Clínica Dental</h1>
              <h2>Dr. ${auth.user?.name || 'Nombre del Doctor'}</h2>
              <div class="doctor-credentials">
                Cirujano Dentista<br>
                Cédula Profesional: XXXXXXXXX
              </div>
            </div>
          </div>

          <h1 class="certificate-title">Certificado Dental</h1>

          <div class="certificate-content">
            <p>
              Por medio del presente documento, <strong>CERTIFICO</strong> que el/la paciente 
              <span class="patient-name">${patientName}</span>, 
              ${patient?.idNumber ? `con documento de identidad No. ${patient.idNumber},` : ''}
              ha sido examinado(a) y tratado(a) en esta clínica dental.
            </p>

            ${certificate.procedures && certificate.procedures.length > 0 ? `
              <p>
                <strong>Procedimiento${certificate.procedures.length > 1 ? 's' : ''} realizado${certificate.procedures.length > 1 ? 's' : ''}:</strong> 
                ${certificate.procedures.map(proc => `${proc.procedureName}${proc.quantity > 1 ? ` (${proc.quantity} ${proc.quantity > 1 ? 'sesiones' : 'sesión'})` : ''}`).join(', ')}.
              </p>
            ` : ''}

            ${certificate.observations ? `
              <p>
                <strong>Observaciones médicas:</strong> ${certificate.observations}
              </p>
            ` : ''}

            <p>
              Se extiende el presente certificado a solicitud del interesado para los fines que estime convenientes.
            </p>
          </div>

          <div class="signature-section">
            <div class="date-section">
              <p><strong>Fecha:</strong> ${certificateDate}</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="doctor-name">Dr. ${auth.user?.name || 'Nombre del Doctor'}</div>
              <div class="doctor-title">Cirujano Dentista</div>
              <div class="doctor-title">Cédula: XXXXXXXXX</div>
            </div>
          </div>

          <div class="certificate-footer">
            <div class="contact-info">
              <strong>Clínica Dental</strong><br>
              Dirección: Calle Principal #123, Ciudad<br>
              Teléfono: (XX) XXXX-XXXX | Email: contacto@clinica.com<br>
              www.clinicadental.com
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  if (isLoadingData) {
    return (
      <div className="new-certificate-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="new-certificate-container">
        <PatientSectionHeader
          patientId={patientId}
          patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
          sectionTitle="Nuevo Certificado Dental"
          onBackClick={handleCancel}
        />
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="new-certificate-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Nuevo Certificado Dental"
        onBackClick={handleCancel}
      />

      <div className="certificate-form-content">
        {message.text && (
          <div className={`message ${message.type}`}>
            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="certificate-form">
          <div className="form-section">
            <h3>Información del Certificado</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">
                  <i className="fas fa-calendar"></i>
                  Fecha del Certificado *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>
                  <i className="fas fa-tooth"></i>
                  Procedimientos Realizados *
                </label>
                <MultiProcedureSelector
                  selectedProcedures={formData.procedures}
                  onProceduresChange={handleProceduresChange}
                  placeholder="Buscar y agregar procedimientos..."
                  showCost={false}
                />
                <small className="field-hint">
                  Puede seleccionar múltiples procedimientos y especificar la cantidad de cada uno.
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="observations">
                  <i className="fas fa-sticky-note"></i>
                  Observaciones Médicas
                </label>
                <textarea
                  id="observations"
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  placeholder="Escriba aquí cualquier observación adicional sobre el tratamiento o estado del paciente..."
                  rows="4"
                />
                <small className="field-hint">
                  Opcional. Incluya cualquier información relevante que deba aparecer en el certificado.
                </small>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              icon="fas fa-eye"
              onClick={handlePreview}
              disabled={isLoading || !formData.procedures || formData.procedures.length === 0}
            >
              Vista Previa
            </Button>

            <Button
              type="submit"
              variant="primary"
              icon={isLoading ? "fas fa-spinner fa-spin" : "fas fa-save"}
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : 'Crear Certificado'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCertificate;
